import PropTypes from 'prop-types';
import useLiterals from 'utils/hooks/useLiterals';
import { translate } from 'utils/translate';
import { formatCurrency } from 'utils/formatCurrency';
import { PROVIDER_TYPE, PAYCASH_MAX_AMOUNT } from 'constants/global';
import bbvaLogo from 'assets/logos/bbva.svg';
import mercadoPagoLogo from 'assets/images/mercado_pago.jpg';
import { ReactComponent as CashIcon } from 'assets/icons/checkout/cash.svg';
import { ReactComponent as LockIcon } from 'assets/icons/checkout/lock.svg';
import { ReactComponent as ShieldIcon } from 'assets/icons/checkout/shield.svg';
import { ReactComponent as WalletCardIcon } from 'assets/icons/checkout/wallet-card.svg';
import { isMethodSelectable } from './hooks/usePaymentMethods';
import MethodCard from './MethodCard';
import MethodDetails from './MethodDetails';
import PartnerStoresGrid from './PartnerStoresGrid';

const ACTIVATION_STATUS = ['idle', 'loading', 'ready', 'error'];

const CARD_PROVIDERS = [PROVIDER_TYPE.BANCOMER, PROVIDER_TYPE.MERCADO_PAGO];
const CASH_PROVIDERS = [PROVIDER_TYPE.PAYCASH, PROVIDER_TYPE.PUNTORED];

const LITERAL_KEY = {
  [PROVIDER_TYPE.BANCOMER]: 'bbva',
  [PROVIDER_TYPE.MERCADO_PAGO]: 'mercadoPago',
  [PROVIDER_TYPE.PAYCASH]: 'paycash',
  [PROVIDER_TYPE.PUNTORED]: 'puntored',
};

const SECTION_TITLE_CLASSES = 'mb-3 text-sm font-semibold uppercase tracking-wide text-primary-700';
const GRID_CLASSES = 'grid grid-cols-1 items-start gap-4 md:grid-cols-2';

const renderLogo = (provider) => {
  if (provider === PROVIDER_TYPE.BANCOMER) {
    return <img src={bbvaLogo} alt='BBVA' className='h-auto w-16 sm:w-20' />;
  }
  if (provider === PROVIDER_TYPE.MERCADO_PAGO) {
    return <img src={mercadoPagoLogo} alt='Mercado Pago' className='h-auto w-16' />;
  }
  return <CashIcon aria-hidden='true' className='h-10 w-10 text-primary-700' />;
};

const buildDetailRows = (provider, details) => {
  const isBbva = provider === PROVIDER_TYPE.BANCOMER;
  return [
    { icon: 'lock', title: details.secureTitle, text: isBbva ? details.secureBbva : details.secureMercadoPago },
    { icon: 'card', title: details.cardsTitle, text: isBbva ? details.cardsBbva : details.cardsMercadoPago },
    { icon: 'shield', title: details.privacyTitle, text: details.privacyText },
  ];
};

const PaymentMethodSelector = ({
  methods,
  selected = null,
  onSelect,
  activation = { status: 'idle' },
  onRetry = () => {},
  onPay,
  payDisabled = false,
  className = '',
}) => {
  const literals = useLiterals('paymentCheckout');
  const status = activation?.status || 'idle';
  const isLoading = status === 'loading';
  const hasError = status === 'error';

  const isVisible = (provider) => Boolean(methods?.[provider]?.visible);
  const cardProviders = CARD_PROVIDERS.filter(isVisible);
  const cashProviders = CASH_PROVIDERS.filter(isVisible);

  const canPay = isMethodSelectable(methods, selected) && !hasError && !isLoading && !payDisabled;

  const renderError = (provider) => (
    <div role='alert' className='rounded-xl bg-primary-50 p-3 text-[13px] text-primary-800'>
      <p className='m-0'>
        {provider === PROVIDER_TYPE.MERCADO_PAGO
          ? literals.activationError.mercadoPago
          : literals.activationError.generic}
      </p>
      <button
        type='button'
        onClick={onRetry}
        className='mt-2 border-0 bg-transparent p-0 text-sm font-semibold text-primary-700 underline'
      >
        {literals.retry}
      </button>
    </div>
  );

  const renderCard = (provider) => {
    const { disabled } = methods[provider];
    const isSelected = selected === provider;
    const isCardMethod = CARD_PROVIDERS.includes(provider);
    const isPaycash = provider === PROVIDER_TYPE.PAYCASH;
    const copy = literals.methods[LITERAL_KEY[provider]];
    const showDetails = isSelected && isCardMethod;
    const showStores = isSelected && !isCardMethod;
    const showError = isSelected && hasError;

    return (
      <MethodCard
        key={provider}
        provider={provider}
        logo={renderLogo(provider)}
        eyebrow={copy.eyebrow}
        title={copy.title}
        description={copy.description || null}
        note={isPaycash && disabled
          ? translate(literals.paycashMaxAmount, { amount: formatCurrency(PAYCASH_MAX_AMOUNT) })
          : null}
        badge={isPaycash && !disabled ? literals.recommended : null}
        recommended={isPaycash && !disabled}
        selected={isSelected}
        disabled={disabled}
        expandable
        onSelect={onSelect}
      >
        {(showDetails || showStores || showError) && (
          <div className='space-y-3'>
            {showDetails && <MethodDetails rows={buildDetailRows(provider, literals.details)} />}
            {showStores && <PartnerStoresGrid provider={provider} title={literals.sections.stores} />}
            {showError && renderError(provider)}
          </div>
        )}
      </MethodCard>
    );
  };

  return (
    <div className={`w-full min-w-0 ${className}`}>
      <div className='mb-6 flex items-center gap-4'>
        <span className='flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-white text-primary-700 shadow-card lg:hidden'>
          <WalletCardIcon aria-hidden='true' className='h-7 w-7' />
        </span>
        <div className='min-w-0'>
          <h2 className='m-0 font-display text-[22px] font-medium leading-tight text-neutral-900'>{literals.heading.title}</h2>
          <p className='m-0 mt-1 text-base text-neutral-500'>{literals.heading.subtitle}</p>
        </div>
      </div>

      <div role='radiogroup' aria-label={literals.heading.title} className='space-y-6'>
        {cardProviders.length > 0 && (
          <section>
            <h3 className={SECTION_TITLE_CLASSES}>{literals.sections.card}</h3>
            <div className={GRID_CLASSES}>{cardProviders.map(renderCard)}</div>
          </section>
        )}
        {cashProviders.length > 0 && (
          <section>
            <h3 className={SECTION_TITLE_CLASSES}>{literals.sections.cash}</h3>
            <div className={GRID_CLASSES}>{cashProviders.map(renderCard)}</div>
          </section>
        )}
      </div>

      <div className='mt-8 flex flex-col items-center gap-4 lg:flex-row lg:gap-12'>
        <button
          type='button'
          onClick={onPay}
          disabled={!canPay}
          aria-busy={isLoading ? 'true' : undefined}
          className='flex h-14 w-full items-center justify-center gap-3 rounded-[14px] border-0 bg-primary-800 px-10 text-lg font-semibold text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-50 lg:w-auto lg:min-w-[255px]'
        >
          {isLoading ? (
            <span
              data-testid='pay-spinner'
              aria-hidden='true'
              className='h-5 w-5 animate-spin rounded-full border-2 border-solid border-white border-t-transparent'
            />
          ) : (
            <LockIcon aria-hidden='true' className='h-5 w-5' />
          )}
          {literals.pay}
        </button>
        <p className='m-0 flex items-center gap-2 text-sm text-neutral-500'>
          <ShieldIcon aria-hidden='true' className='h-4 w-4 shrink-0 text-neutral-400 lg:text-gold-500' />
          <span className='lg:hidden'>{literals.dataProtected}</span>
          <span className='hidden lg:inline'>{literals.dataProtectedSsl}</span>
        </p>
      </div>
    </div>
  );
};

PaymentMethodSelector.propTypes = {
  methods: PropTypes.objectOf(PropTypes.shape({
    visible: PropTypes.bool,
    disabled: PropTypes.bool,
  })).isRequired,
  selected: PropTypes.oneOf([...Object.values(PROVIDER_TYPE), null]),
  onSelect: PropTypes.func.isRequired,
  activation: PropTypes.shape({
    status: PropTypes.oneOf(ACTIVATION_STATUS),
  }),
  onRetry: PropTypes.func,
  onPay: PropTypes.func.isRequired,
  payDisabled: PropTypes.bool,
  className: PropTypes.string,
};

export default PaymentMethodSelector;
